import { addWebSyncTodo, updateWebSyncTodo } from '@/api/api'
import mobileDailyTodoEditorTexts from '@/utils/mobileDailyTodoEditorTexts'

export default {
  data() {
    return {
      dailyTodoEditorOpen: false,
      dailyTodoEditorTarget: null,
      dailyTodoEditorContent: '',
      dailyTodoEditorRemark: '',
      dailyTodoEditorRemarkUrl: '',
      dailyTodoEditorRemarkImageMode: false,
      dailyTodoEditorLevel: 100,
      dailyTodoEditorDate: '',
      dailyTodoEditorOriginalDate: '',
      dailyTodoEditorCalendarOpen: false,
      dailyTodoEditorCalendarCursorDate: new Date(),
      dailyTodoEditorImageMode: false,
      dailyTodoEditorImageUrl: '',
      dailyTodoEditorSubmitting: false,
      dailyTodoEditorDeleteOpen: false
    }
  },
  computed: {
    dailyTodoEditLabel() {
      return this.ui.edit || '\u7f16\u8f91'
    },
    showDailyBottomEditAction() {
      return this.isDailyTodoTab && this.dailySelectedTodos.length == 1
    },
    dailyMobileDetailOpen() {
      return this.dailyPreviewOpen || this.dailyTodoEditorOpen
    },
    dailyTodoEditorTitle() {
      return this.ui.todoContent || '\u5f85\u529e\u5185\u5bb9'
    },
    dailyTodoEditorRemarkLabels() {
      return mobileDailyTodoEditorTexts[this.normalizedLanguage] || mobileDailyTodoEditorTexts.us
    },
    canDeleteDailyTodoEditorRemark() {
      return !this.dailyTodoEditorSubmitting && !this.dailyTodoEditorDeleteOpen
        && !!(String(this.dailyTodoEditorRemark || '').trim() || this.dailyTodoEditorRemarkUrl)
    },
    dailyTodoEditorDateLabel() {
      const labels = { zh: '\u65e5\u671f', tw: '\u65e5\u671f', nl: 'Datum', uk: 'Date', us: 'Date', fr: 'Date', de: 'Datum', it: 'Data', ja: '\u65e5\u4ed8', ko: '\ub0a0\uc9dc', pt: 'Data', ru: '\u0414\u0430\u0442\u0430', es: 'Fecha' }
      return this.ui.date || labels[this.normalizedLanguage] || 'Date'
    },
    dailyTodoEditorConfirmLabel() {
      return this.normalizedLanguage == 'zh' ? '\u786e\u8ba4' : (this.normalizedLanguage == 'tw' ? '\u78ba\u8a8d' : this.ui.ok)
    },
    dailyTodoEditorWeekdayLabels() {
      return this.isCjkDailyLanguage ? ['Sun', 'Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat'] : this.dailyWeekdayLabels
    },
    dailyTodoEditorLevelOptions() {
      return [
        { level: 0, label: this.ui.todoLevel0, icon: this.paperIcons.todoAddLevel0, activeIcon: this.paperIcons.todoAddLevel0Active },
        { level: 1, label: this.ui.todoLevel1, icon: this.paperIcons.todoAddLevel1, activeIcon: this.paperIcons.todoAddLevel1Active },
        { level: 2, label: this.ui.todoLevel2, icon: this.paperIcons.todoAddLevel2, activeIcon: this.paperIcons.todoAddLevel2Active }
      ]
    },
    canSubmitDailyTodoEditor() {
      if (!this.dailyTodoEditorOpen || this.dailyTodoEditorSubmitting || this.dailyTodoEditorDeleteOpen) {
        return false
      }
      if (!this.normalizeDailyDateValue(this.dailyTodoEditorDate)) {
        return false
      }
      return (this.dailyTodoEditorTarget && this.dailyTodoEditorImageMode)
        || String(this.dailyTodoEditorContent || '').trim().length > 0
    },
    dailyTodoEditorDisplayDate() {
      const date = this.parseDailyDate(this.dailyTodoEditorDate)
      if (!date) {
        return ''
      }
      return `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`
    },
    dailyTodoEditorCalendarTitle() {
      const date = this.toValidDate(this.dailyTodoEditorCalendarCursorDate) || new Date()
      const template = this.ui.dailyCalendarMonthFormat || (this.isCjkDailyLanguage ? '{year} \u5e74 {month} \u6708' : '{monthName} {year}')
      const locales = { zh: 'zh-CN', tw: 'zh-TW', us: 'en-US', uk: 'en-GB' }
      const monthName = new Intl.DateTimeFormat(locales[this.normalizedLanguage] || this.normalizedLanguage || 'en-US', { month: 'long' }).format(date)
      return template.replace('{year}', date.getFullYear()).replace('{month}', date.getMonth() + 1).replace('{monthName}', monthName)
    },
    dailyTodoEditorCalendarCells() {
      const current = this.toValidDate(this.dailyTodoEditorCalendarCursorDate) || new Date()
      const firstDay = new Date(current.getFullYear(), current.getMonth(), 1)
      const start = new Date(firstDay)
      start.setDate(firstDay.getDate() - this.getDailyWeekStartOffset(firstDay))
      const today = this.formatDailyDateValue(new Date())
      const selected = this.normalizeDailyDateValue(this.dailyTodoEditorDate)
      const cells = []
      for (let i = 0; i < 42; i++) {
        const date = new Date(start)
        date.setDate(start.getDate() + i)
        const value = this.formatDailyDateValue(date)
        cells.push({
          key: `todo-editor-${value}`,
          dateValue: value,
          day: date.getDate(),
          currentMonth: date.getMonth() == current.getMonth(),
          today: value == today,
          selected: value == selected
        })
      }
      return cells
    },
    dailyTodoEditorDeleteConfirmText() {
      return this.ui.dailyTodoDeleteConfirm || '\u5220\u9664\u540e\u5c06\u65e0\u6cd5\u6062\u590d\uff0c\u662f\u5426\u5220\u9664\uff1f'
    },
    dailyTodoEditorDeleteTitle() {
      return this.ui.deleteConfirmTitle || (this.normalizedLanguage == 'zh' ? '\u5220\u9664\u786e\u8ba4' : (this.normalizedLanguage == 'tw' ? '\u522a\u9664\u78ba\u8a8d' : this.ui.delete))
    }
  },
  watch: {
    dailyTodoEditorContent() {
      this.$nextTick(this.resizeDailyTodoEditorInputs)
    },
    dailyTodoEditorRemark() {
      this.$nextTick(this.resizeDailyTodoEditorInputs)
    },
    dailyTodoEditorOpen(open) {
      if (open) {
        this.$nextTick(this.resizeDailyTodoEditorInputs)
      }
    }
  },
  mounted() {
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.resizeDailyTodoEditorInputs)
    }
  },
  beforeDestroy() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.resizeDailyTodoEditorInputs)
    }
  },
  methods: {
    resizeDailyTodoEditorInput(event) {
      const input = event && event.target ? event.target : event
      if (!input || !input.style) {
        return
      }
      input.style.height = 'auto'
      input.style.height = `${Math.max(128, input.scrollHeight || 0)}px`
    },
    resizeDailyTodoEditorInputs() {
      if (!this.dailyTodoEditorOpen) {
        return
      }
      this.resizeDailyTodoEditorInput(this.$refs.dailyTodoEditorContentInput)
      this.resizeDailyTodoEditorInput(this.$refs.dailyTodoEditorRemarkInput)
    },
    handleDailyBottomEdit() {
      if (!this.showDailyBottomEditAction || this.interactionLocked || this.dailyTodoEditorSubmitting) {
        return
      }
      this.openDailyTodoEditor(this.dailySelectedTodos[0])
    },
    openDailyTodoEditor(item = null) {
      if (this.dailyTodoEditorSubmitting) {
        return
      }
      const date = item
        ? this.normalizeDailyDateValue(item.date) || this.formatDailyDateValue(new Date())
        : this.normalizeDailyDateValue(this.dailySelectedDate) || this.formatDailyDateValue(new Date())
      const content = item ? this.getDailyTodoEditorOriginalContent(item) : ''
      const imageUrl = item && !content ? this.getDailyTodoEditorOriginalImageUrl(item) : ''
      const remark = item ? this.getDailyTodoEditorOriginalRemark(item) : ''
      const remarkUrl = item ? this.getDailyTodoEditorOriginalRemarkUrl(item) : ''
      const level = Number(item && item.level)
      this.dailyTabMenuOpen = false
      this.dailyPeriodMenuOpen = false
      this.dailyFinishMenuOpen = false
      this.moreMenuId = ''
      if (typeof this.cancelMobilePullRefreshGesture == 'function') {
        this.cancelMobilePullRefreshGesture()
      }
      this.dailyTodoEditorTarget = item
      this.dailyTodoEditorContent = content
      this.dailyTodoEditorRemark = remark
      this.dailyTodoEditorRemarkUrl = remarkUrl
      this.dailyTodoEditorRemarkImageMode = !remark && !!remarkUrl
      this.dailyTodoEditorLevel = item && [0, 1, 2].includes(level) ? level : 100
      this.dailyTodoEditorDate = date
      this.dailyTodoEditorOriginalDate = date
      this.dailyTodoEditorImageUrl = imageUrl
      this.dailyTodoEditorImageMode = !content && !!imageUrl
      this.dailyTodoEditorCalendarCursorDate = this.parseDailyDate(date) || new Date()
      this.dailyTodoEditorCalendarOpen = false
      this.dailyTodoEditorDeleteOpen = false
      this.dailyTodoEditorOpen = true
      this.$nextTick(() => {
        this.resizeDailyTodoEditorInputs()
        const dialog = this.$refs.dailyTodoEditorDialog
        if (dialog && typeof dialog.focus == 'function') {
          dialog.focus()
        }
      })
    },
    closeDailyTodoEditor() {
      if (this.dailyTodoEditorSubmitting) {
        return
      }
      this.resetDailyTodoEditor()
    },
    resetDailyTodoEditor() {
      this.dailyTodoEditorOpen = false
      this.dailyTodoEditorCalendarOpen = false
      this.dailyTodoEditorDeleteOpen = false
      this.dailyTodoEditorTarget = null
      this.dailyTodoEditorContent = ''
      this.dailyTodoEditorRemark = ''
      this.dailyTodoEditorRemarkUrl = ''
      this.dailyTodoEditorRemarkImageMode = false
      this.dailyTodoEditorLevel = 100
      this.dailyTodoEditorImageUrl = ''
      this.dailyTodoEditorImageMode = false
    },
    removeDailyTodoEditorRemark() {
      if (!this.canDeleteDailyTodoEditorRemark) {
        return
      }
      this.dailyTodoEditorRemark = ''
      this.dailyTodoEditorRemarkUrl = ''
      this.dailyTodoEditorRemarkImageMode = false
      this.$nextTick(this.resizeDailyTodoEditorInputs)
    },
    selectDailyTodoEditorLevel(level) {
      if (this.dailyTodoEditorSubmitting || this.dailyTodoEditorDeleteOpen) {
        return
      }
      const value = Number(level)
      this.dailyTodoEditorLevel = [0, 1, 2].includes(value) ? value : 100
    },
    toggleDailyTodoEditorCalendar() {
      if (this.dailyTodoEditorSubmitting || this.dailyTodoEditorDeleteOpen) {
        return
      }
      this.dailyTodoEditorCalendarOpen = !this.dailyTodoEditorCalendarOpen
      if (this.dailyTodoEditorCalendarOpen) {
        this.dailyTodoEditorCalendarCursorDate = this.parseDailyDate(this.dailyTodoEditorDate) || new Date()
      }
    },
    changeDailyTodoEditorCalendarMonth(offset) {
      if (this.dailyTodoEditorSubmitting || this.dailyTodoEditorDeleteOpen) {
        return
      }
      const current = this.toValidDate(this.dailyTodoEditorCalendarCursorDate) || new Date()
      this.dailyTodoEditorCalendarCursorDate = new Date(current.getFullYear(), current.getMonth() + offset, 1)
    },
    selectDailyTodoEditorDate(value) {
      if (this.dailyTodoEditorSubmitting || this.dailyTodoEditorDeleteOpen) {
        return
      }
      const date = this.normalizeDailyDateValue(value)
      if (!date) {
        return
      }
      this.dailyTodoEditorDate = date
      this.dailyTodoEditorCalendarCursorDate = this.parseDailyDate(date) || this.dailyTodoEditorCalendarCursorDate
      this.dailyTodoEditorCalendarOpen = false
    },
    async confirmDailyTodoEditor() {
      if (!this.dailyTodoEditorOpen || this.dailyTodoEditorSubmitting || this.dailyTodoEditorDeleteOpen) {
        return
      }
      if (!this.dailyTodoEditorTarget && !String(this.dailyTodoEditorContent || '').trim()) {
        if (this.$message && typeof this.$message.warning == 'function') {
          this.$message.warning(this.dailyTodoEditorRemarkLabels.contentRequired)
        }
        const input = this.$refs.dailyTodoEditorContentInput
        if (input && typeof input.focus == 'function') {
          input.focus()
        }
        return
      }
      if (!this.canSubmitDailyTodoEditor) {
        return
      }
      const item = this.dailyTodoEditorTarget
      const date = this.normalizeDailyDateValue(this.dailyTodoEditorDate)
      const content = item && this.dailyTodoEditorImageMode
        ? this.getDailyTodoEditorOriginalContent(item)
        : String(this.dailyTodoEditorContent || '').trim()
      const level = [0, 1, 2].includes(Number(this.dailyTodoEditorLevel)) ? Number(this.dailyTodoEditorLevel) : 100
      const remark = String(this.dailyTodoEditorRemark || '').trim()
      let params = { level, date: this.formatDailyLooseDateValue(date), content, remark }
      if (item) {
        if (item.date === undefined || item.date === null || item.date === '') {
          this.showDailyTodoEditorFailedMessage(new Error('\u7f3a\u5c11\u66f4\u65b0\u53c2\u6570'), true)
          return
        }
        const originalDate = this.normalizeDailyDateValue(this.dailyTodoEditorOriginalDate)
        const changedDate = this.parseDailyDate(date)
        params = {
          date: this.formatDailyLooseDateValue(item.date),
          id: this.getDailyTodoUpdateId(item),
          content,
          remark,
          level,
          finish: item.isFinish !== undefined && item.isFinish !== null && item.isFinish !== '' ? item.isFinish : 0,
          newDate: date != originalDate ? `${changedDate.getFullYear()}-${changedDate.getMonth() + 1}-${changedDate.getDate()}` : ''
        }
      }
      this.dailyTodoEditorSubmitting = true
      try {
        const ret = await (item ? updateWebSyncTodo : addWebSyncTodo)(params, { skipGlobalErrorMessage: true })
        if (!this.isDailyTodoEditorSuccessResponse(ret)) {
          throw ret
        }
        this.resetDailyTodoEditor()
        if (this.$message && typeof this.$message.success == 'function') {
          this.$message.success(item ? '\u4fee\u6539\u6210\u529f' : '\u6dfb\u52a0\u6210\u529f')
        }
        this.dailyCalendarMarkerCache = {}
        this.loadDailyDashboard()
      } catch (error) {
        this.showDailyTodoEditorFailedMessage(error, !!item)
      } finally {
        this.dailyTodoEditorSubmitting = false
      }
    },
    openDailyTodoEditorDelete() {
      if (this.dailyTodoEditorSubmitting || this.dailyTodoEditorCalendarOpen || !this.dailyTodoEditorTarget) {
        return
      }
      this.dailyTodoEditorDeleteOpen = true
      this.$nextTick(() => {
        const dialog = this.$refs.dailyTodoEditorDeleteDialog
        if (dialog && typeof dialog.focus == 'function') {
          dialog.focus()
        }
      })
    },
    closeDailyTodoEditorDelete() {
      if (this.dailyTodoEditorSubmitting) {
        return
      }
      this.dailyTodoEditorDeleteOpen = false
      this.$nextTick(() => {
        const button = this.$refs.dailyTodoEditorDeleteButton
        if (button && typeof button.focus == 'function') {
          button.focus()
        }
      })
    },
    trapDailyTodoEditorFocus(event) {
      if (!event || typeof document === 'undefined') {
        return
      }
      const dialog = this.dailyTodoEditorDeleteOpen ? this.$refs.dailyTodoEditorDeleteDialog : this.$refs.dailyTodoEditorDialog
      if (!dialog || typeof dialog.querySelectorAll != 'function') {
        return
      }
      const focusable = Array.from(dialog.querySelectorAll('button, input, textarea, select, [tabindex="0"]')).filter(element => {
        if (element.disabled || element.tabIndex < 0 || element.getAttribute('aria-disabled') == 'true' || element.closest('[inert]')) {
          return false
        }
        if (!element.getClientRects().length) {
          return false
        }
        return typeof window === 'undefined' || typeof window.getComputedStyle != 'function' || window.getComputedStyle(element).visibility != 'hidden'
      })
      if (!focusable.length) {
        event.preventDefault()
        return
      }
      const active = document.activeElement
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      const outside = active === dialog || !dialog.contains(active)
      if (outside || (event.shiftKey ? active === first : active === last)) {
        event.preventDefault()
        const target = event.shiftKey ? last : first
        target.focus()
      }
    },
    async confirmDailyTodoEditorDelete() {
      if (this.dailyTodoEditorSubmitting || !this.dailyTodoEditorDeleteOpen || !this.dailyTodoEditorTarget) {
        return
      }
      const item = this.dailyTodoEditorTarget
      this.dailyTodoEditorSubmitting = true
      try {
        await this.deleteDailyItem(item)
        this.resetDailyTodoEditor()
        this.clearDailySelection()
        this.showDailyDeleteSuccessMessage()
        this.dailyCalendarMarkerCache = {}
        this.loadDailyDashboard()
      } catch (error) {
        this.showDailyDeleteFailedMessage(null, error, 'todo')
      } finally {
        this.dailyTodoEditorSubmitting = false
      }
    },
    formatDailyLooseDateValue(value) {
      const date = value instanceof Date ? value : this.parseDailyDate(value)
      if (!date || Number.isNaN(date.getTime())) {
        return ''
      }
      return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`
    },
    getDailyTodoUpdateId(item) {
      const raw = item && item.raw && typeof item.raw == 'object' ? item.raw : {}
      const rawId = this.pickString(raw, ['id', 'uuid', 'resourceId', 'dailyId'])
      if (rawId) {
        return rawId
      }
      const itemId = item && item.id !== undefined && item.id !== null ? String(item.id).trim() : ''
      return itemId && !/^daily-[^-]+-\d+$/.test(itemId) ? itemId : ''
    },
    getDailyTodoEditorOriginalContent(item) {
      const raw = item && item.raw && typeof item.raw == 'object' ? item.raw : {}
      const rawValue = this.pickValue(raw, ['content', 'text', 'description', 'summary'])
      if (rawValue !== undefined && rawValue !== null) {
        return String(rawValue).trim()
      }
      // Normalization uses a display name for empty content; image todos must keep their original empty payload.
      if (Number(item && item.classification) === 1 && this.getDailyTodoEditorOriginalImageUrl(item)) {
        return ''
      }
      return item && item.content !== undefined && item.content !== null ? String(item.content).trim() : ''
    },
    getDailyTodoEditorOriginalImageUrl(item) {
      const raw = item && item.raw && typeof item.raw == 'object' ? item.raw : {}
      return this.pickDailySourceImageUrl(raw)
        || this.pickDailyPageImageUrl(raw)
        || (item && item.imageUrl ? String(item.imageUrl).trim() : '')
        || (item && item.url ? String(item.url).trim() : '')
    },
    getDailyTodoEditorOriginalRemark(item) {
      const raw = item && item.raw && typeof item.raw == 'object' ? item.raw : item
      return raw && raw.remark !== undefined && raw.remark !== null ? String(raw.remark).trim() : ''
    },
    getDailyTodoEditorOriginalRemarkUrl(item) {
      const raw = item && item.raw && typeof item.raw == 'object' ? item.raw : item
      return raw && raw.remarkUrl !== undefined && raw.remarkUrl !== null ? String(raw.remarkUrl).trim() : ''
    },
    isDailyTodoEditorSuccessResponse(source) {
      const code = this.getPaperApiCode(source)
      return code === undefined || code === null || code === '' || code == 200 || code == '200'
    },
    showDailyTodoEditorFailedMessage(error, editing) {
      if (this.$message && typeof this.$message.error == 'function') {
        this.$message.error(this.getPaperApiErrorMessage(error) || (editing ? '\u4fee\u6539\u5931\u8d25' : '\u6dfb\u52a0\u5931\u8d25'))
      }
    }
  }
}
